import { PEER } from '../../constants'

// Utils.
import iceCollectionScheduledCheck from '../utils/iceCollectionScheduledCheck'

/**
 * Sets an SDP as the local description of the connection.
 * The returned Promise will resolve when the Peer is ready for negotiation,
 *    taking into account the Peer's `trickleIceMode` configuration.
 * @method setLocalDescription
 * @param  {RTCSessionDescription} sessionDesc
 * @return {Promise}
 */
export default function setLocalDescription (desc) {
  const { nativePeer, proxyPeer, config, id, emitter, iceTimer, log } = this

  // TODO: SDP pipeline here.
  log.info(`Setting local description ${desc.type} in ${proxyPeer.signalingState} state.`)
  log.debug(`Setting local description ${desc.type}:`, desc.sdp)

  /**
   * Scenario: A local answer SDP is being applied to the Peer, but it does
   *    not have a selected DTLS role yet. This should occur during initial
   *    negotiation, before responding with this Peer's answer.
   * Set the local Peer's DTLS role depending on what role was generated. This
   *    role will be kept throughout all renegotiations.
   */
  if (!this.dtlsRole && desc.type === 'answer') {
    const dtlsMatch = desc.sdp.match(/a=setup:(\w*?)[\r\n]/)
    if (dtlsMatch) {
      log.debug(`Selecting DTLS role ${dtlsMatch[1]}.`)
      this.dtlsRole = dtlsMatch[1]
    }
  }

  return new Promise((resolve, reject) => {
    // We always want to wait for the PeerConnection to be ready for
    //    negotiation before resolving setLocalDescription.
    // Each trickle ICE option (FULL/HALF/NONE) emits "negotiation ready" event once.
    emitter.once('onnegotiationready', () => {
      if (iceTimer.isStarted()) {
        // In a HALF trickle scenario, the Peer will be ready for negotiation
        //    before ICE collection has completed. Log that timing.
        log.debug(`Took ${iceTimer.timeFromStart()}ms to collect ICE candidates before negotiation.`)
      }
      resolve()
    })

    nativePeer
      .setLocalDescription(desc)
      .then(() => {
        log.info('Finished setting local description.')
        log.debug(`State is now ${proxyPeer.signalingState}.`)

        if (config.trickleIceMode === PEER.TRICKLE_ICE.FULL) {
          // Trickling ICE candidates means that we can begin negotiation immediately.
          log.debug('Ready for negotiation (full trickleICE).')
          emitter.emit('onnegotiationready')
        } else {
          // ICE candidates aren't always gathered (only initially and when something
          //    changes), but we rely on "gathering complete" to know when the Peer is
          //    ready for negotiation. Give the Peer some time to start gathering
          //    before deciding if we need to wait for gathering to complete or not.
          // The timeout is needed because of a bug in Chrome:
          //    https://bugs.chromium.org/p/webrtc/issues/detail?id=1873
          // Known issue: If candidate collection takes less time than this timeout,
          //    the logged message will be incorrect, but will functionally still work.
          setTimeout(() => {
            if (proxyPeer.iceGatheringState === 'complete') {
              // Gathering is "complete", so we are ready for negotiation.
              log.debug('Ready for negotiation; ICE candidate collection not needed.')
              emitter.emit('onnegotiationready')
            } else {
              log.debug(`Waiting for ICE collection process (${config.trickleIceMode}).`)
              // Register the ice collection check function callback to start the candidate collection
              //  looping process
              proxyPeer.isIceCollectionCheckOngoing = true
              iceCollectionScheduledCheck(this)
            }
          }, 25)
        }
      })
      .catch(err => {
        log.info('Failed to set local description.')
        log.debug(`Peer ${id}: ${err}`)
        // Parse native error. Make it more understand and/or
        //    provide a better log about what went wrong.
        reject(err)
      })
  })
}
